/**
 * Master tool registry — imports all category files and provides lookup functions.
 */
import type { ToolConfig, CategorySlug } from "./types";
import { CATEGORIES, CATEGORY_MAP } from "./categories";

import { propertyTools } from "./calculators/property";
import { loanTools } from "./calculators/loans";
import { taxTools } from "./calculators/tax";
import { investmentTools } from "./calculators/investment";
import { retirementTools } from "./calculators/retirement";
import { salaryTools } from "./calculators/salary";
import { businessTools } from "./calculators/business";
import { rentTools } from "./calculators/rent";
import { insuranceTools } from "./calculators/insurance";
import { legalTools } from "./calculators/legal";
import { savingsTools } from "./calculators/savings";
import { personalFinanceTools } from "./calculators/personal-finance";
import { nriTools } from "./calculators/nri";
import { educationTools } from "./calculators/education";
import { vehicleTools } from "./calculators/vehicle";
import { goldTools } from "./calculators/gold";

/** Every tool across all categories */
export const ALL_TOOLS: ToolConfig[] = [
  ...propertyTools,
  ...loanTools,
  ...taxTools,
  ...investmentTools,
  ...retirementTools,
  ...salaryTools,
  ...businessTools,
  ...rentTools,
  ...insuranceTools,
  ...legalTools,
  ...savingsTools,
  ...personalFinanceTools,
  ...nriTools,
  ...educationTools,
  ...vehicleTools,
  ...goldTools,
];

/** Map: "category/slug" → ToolConfig */
const toolIndex = new Map<string, ToolConfig>();
ALL_TOOLS.forEach((t) => toolIndex.set(`${t.category}/${t.slug}`, t));

/** Map: category → ToolConfig[] */
const categoryIndex = new Map<CategorySlug, ToolConfig[]>();
ALL_TOOLS.forEach((t) => {
  const arr = categoryIndex.get(t.category) ?? [];
  arr.push(t);
  categoryIndex.set(t.category, arr);
});

/* ─── Public API ─── */

export function getTool(category: string, slug: string): ToolConfig | undefined {
  return toolIndex.get(`${category}/${slug}`);
}

export function getToolsByCategory(category: CategorySlug): ToolConfig[] {
  return categoryIndex.get(category) ?? [];
}

export function getAllSlugs(): { category: string; slug: string }[] {
  return ALL_TOOLS.map((t) => ({ category: t.category, slug: t.slug }));
}

export function getToolCount(): number {
  return ALL_TOOLS.length;
}

export function getCategoryCount(): number {
  return CATEGORIES.length;
}

export function getCategoriesWithCounts(): { category: typeof CATEGORIES[number]; count: number }[] {
  return CATEGORIES.map((c) => ({
    category: c,
    count: getToolsByCategory(c.slug).length,
  }));
}

export function searchTools(query: string): ToolConfig[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return ALL_TOOLS.filter(
    (t) =>
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.keywords.some((k) => k.toLowerCase().includes(q))
  );
}

export { CATEGORIES, CATEGORY_MAP };
